
import { defineComponent, onBeforeMount, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// @new-version-components
import { NvCalendarPeriod, NvPeriodBadge } from '@/new-version/components'

// @types
import type { Period } from '@/new-services/types/periods'
import type { DispatcherSector } from '@/new-services/types/dispatcher-sectors'

// @new-services
import periodsAPI from '@/new-services/periods.service'
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const STATUSES = [
    { id: 41, name: 'Активен' },
    { id: 42, name: 'Неактивен' },
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Loader,
        NvPeriodBadge,
        TheInput,
        TheSelect,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            periods: [] as Array<Period>
        })

        const filters_state = reactive({
            dateFrom: null as string | null,
            dateTo: null as string | null,
            statusId: null as number | null,
            dispatcherSectorIds: [] as number[],
        })

        const filters_state_form$ = useVuelidate({
            dateFrom: { required },
            dateTo: { required }
        }, filters_state)

        const data_state = reactive({
            sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<DispatcherSector>
            }
        })

        const formatDate = (date: Date): string => date.toISOString().split('T')[0]

        const loadSectors = async () => {
            try {
                data_state.sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getDispatcherSectors({ page: 1, limit: 9999 })
                data_state.sectors.list = response.data.data

                data_state.sectors.is_failed = false
            } catch (error) {
                data_state.sectors.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении секторов произошла ошибка' })
            } finally {
                data_state.sectors.is_loading = false
            }
        }

        const loadPeriods = async () => {
            if (state.is_loading) return

            filters_state_form$.value.$touch()
            if (filters_state_form$.value.$invalid) return
            
            try {
                state.is_loading = true

                const response = await periodsAPI.getPeriods({
                    dateFrom: filters_state.dateFrom as string,
                    dateTo: filters_state.dateTo as string,
                    statusId: filters_state.statusId,
                    dispatcherSectorIds: filters_state.dispatcherSectorIds
                })

                state.periods = response.data.data.filter((p) => !p.isStandard)

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении периодов произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            const currentDate = new Date(Date.now())
            filters_state.dateFrom = formatDate(currentDate)
            filters_state.dateTo = formatDate(new Date(currentDate.getFullYear() + 1, 1, 1, 12))

            loadSectors()
            loadPeriods()
        })

        const rangeToCalendarPeriod = (range: {dateFrom: string; dateTo: string;}): NvCalendarPeriod => {
            const dateFromParts = range.dateFrom.split('-')
            const dateToParts = range.dateTo.split('-')

            return [
                new Date(Number(dateFromParts[0]), Number(dateFromParts[1]) - 1, Number(dateFromParts[2]), 12),
                new Date(Number(dateToParts[0]), Number(dateToParts[1]) - 1, Number(dateToParts[2]), 12)
            ]
        }
        
        const onResetFilters = () => {
            if (state.is_loading) return

            const currentDate = new Date(Date.now())
            filters_state.dateFrom = formatDate(currentDate)
            filters_state.dateTo = formatDate(new Date(currentDate.getFullYear() + 1, 1, 1, 12))

            filters_state.statusId = null
            filters_state.dispatcherSectorIds = []

            loadPeriods()
        }

        return {
            state,
            filters_state,
            filters_state_form$,
            data_state,
            loadSectors,
            loadPeriods,
            rangeToCalendarPeriod,
            onResetFilters,
            STATUSES,
        }
    }
})
